import { useTranslation } from 'react-i18next';
import { EnvelopeSimple } from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Button,
  InternalPage,
  NoResultsMessage,
  Spacer,
} from 'theSource/components';
import { InboxItem } from './components';
import { LoadMoreButtonWrapperStyled } from './InboxFeedPageStyled';
import { getDaysAgo } from './utils';
import { useFeedMessagesQuery } from 'theSource/queries';
import { useApiQuery } from 'theSource/utils';
import { setUnreadCount } from '../services';
import { UnreadMessageCountDto } from 'theSource/types';
import { selectIsAdmin } from 'theSource/features/AccessDenied';
import { BackLink } from 'enrollment/components';

export const InboxFeedPage = () => {
  const { t } = useTranslation('pages.inbox');
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectIsAdmin);
  const {
    feedMessages,
    isLoadingFeedMessages,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useFeedMessagesQuery({ enabled: !isAdmin });

  const { data: unreadCount, isLoading: isLoadingUnreadMessages } =
    useApiQuery<UnreadMessageCountDto>({
      queryKey: ['unreadFeedMessages'],
      endpoint: '/feed_messages/unread',
      showErrorSnackbar: false,
    });

  const isLoading = isAdmin ? false : (isLoadingFeedMessages || !feedMessages);

  useEffect(() => {
    if (!isLoadingUnreadMessages) {
      dispatch(setUnreadCount(Number(unreadCount?.data)));
    }
  });

  return (
    <>
      <BackLink
        backLinkValue={t('common:back')}
        internal
        underline={false}
        testId="back-button"
        className="mb-4"
      />
      <InternalPage
        testId="inbox-feed-page"
        title={t('title')}
        isLoading={isLoading}
      >
        <>
          {feedMessages?.length ? (
            <>
              {feedMessages.map((message) => (
                <InboxItem
                  key={message.id}
                  daysAgo={getDaysAgo(message.createdAt)}
                  {...message}
                />
              ))}
              {hasNextPage && (
                <>
                  <Spacer height={8} />
                  <LoadMoreButtonWrapperStyled>
                    <Button
                      variant="secondary"
                      label={t('loadMore')}
                      onClick={fetchNextPage}
                      isLoading={isFetchingNextPage}
                      testId="load-more-button"
                    />
                  </LoadMoreButtonWrapperStyled>
                </>
              )}
            </>
          ) : (
            <>
              <Spacer height={12} />
              <NoResultsMessage
                IconComponent={EnvelopeSimple}
                message={t('emptyInbox')}
                testId="empty-inbox-message"
              />
            </>
          )}
        </>
      </InternalPage>
    </>
  );
};
